import { useTranslation } from '@/hooks/use-translation';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAllo, $fetchAllo } from '@/services/api-allo/client';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';

type Company = components['schemas']['CompanyV2Response'];
type CompanyMember = components['schemas']['CompanyMemberResponse'];
type CompaniesResponse = {
  data?: Company[];
  metadata?: {
    pagination?: { current_page?: number; total_pages?: number };
  };
};

interface UseCreateCompanyMutationOptions {
  onSuccess?: (company: components['schemas']['CompanyResponse']) => void;
  onError?: () => void;
}

interface UseUpdateCompanyMutationOptions {
  onSuccess?: (company: components['schemas']['CompanyResponse']) => void;
  onError?: () => void;
}

/**
 * Hook to update a company (currently supports updating name).
 * Optimistically updates the company in the cache.
 */
export function useUpdateCompanyMutation({
  onSuccess,
  onError,
}: UseUpdateCompanyMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  return $apiAllo.useMutation('patch', '/v1/company/{company}', {
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['get', '/v2/company'] });
      await queryClient.cancelQueries({ queryKey: ['get', '/v2/company/{companyPid}'] });

      const previousCompaniesData = queryClient.getQueriesData<InfiniteData<CompaniesResponse>>({
        queryKey: ['get', '/v2/company'],
      });

      // Optimistically update company name in the companies list
      const companyIdToUpdate = variables.params.path.company;
      const newName = variables.body.name;

      queryClient.setQueriesData<InfiniteData<CompaniesResponse>>(
        { queryKey: ['get', '/v2/company'] },
        (oldData) => {
          if (!oldData || !oldData.pages?.length) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map((company) =>
                company.id === companyIdToUpdate ? { ...company, name: newName } : company,
              ),
            })),
          };
        },
      );

      return { previousCompaniesData };
    },
    onSuccess: (response, variables) => {
      // Track company updated
      analytics.track(ANALYTICS_EVENTS.COMPANIES.UPDATED, {
        company_id: variables.params.path.company,
        field_updated: variables.body.name ? 'name' : 'unknown',
      });

      // Invalidate the company detail query cache (used by company panel)
      if (response.data?.id) {
        queryClient.invalidateQueries({ queryKey: ['get', '/v2/company/{companyPid}'] });
      }

      onSuccess?.(response.data as components['schemas']['CompanyResponse']);
    },
    onError: (_error, _variables, context) => {
      // Rollback optimistic update
      const ctx = context as
        | { previousCompaniesData: [unknown[], InfiniteData<CompaniesResponse> | undefined][] }
        | undefined;
      if (ctx?.previousCompaniesData) {
        for (const [queryKey, data] of ctx.previousCompaniesData) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      toast.error(t('components.companies.form.updateCompanyError', 'Failed to update company'), {
        position: 'top-center',
      });

      onError?.();
    },
  });
}

/**
 * Hook to create a new company.
 * Optimistically adds the company to the cache.
 */
export function useCreateCompanyMutation({
  onSuccess,
  onError,
}: UseCreateCompanyMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  return $apiAllo.useMutation('post', '/v1/company', {
    onSuccess: async (newCompany) => {
      await queryClient.cancelQueries({ queryKey: ['get', '/v2/company'] });

      const newCompanyData = {
        id: newCompany.data?.id,
        name: newCompany.data?.name,
        phone_numbers: [],
        members: [],
        activity: [],
        last_activity_date: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as Company;

      // Update companies cache
      queryClient.setQueriesData<InfiniteData<CompaniesResponse>>(
        { queryKey: ['get', '/v2/company'] },
        (oldData) => {
          if (!oldData || !oldData.pages?.length) {
            return oldData;
          }

          const firstPage = oldData.pages[0];
          if (!firstPage) {
            return oldData;
          }

          return {
            ...oldData,
            pages: [
              { ...firstPage, data: [newCompanyData, ...(firstPage.data ?? [])] },
              ...oldData.pages.slice(1),
            ],
          };
        },
      );

      toast.success(t('components.companies.form.createCompanySuccess'), {
        position: 'top-center',
      });

      onSuccess?.(newCompany.data as components['schemas']['CompanyResponse']);
    },
    onError: () => {
      toast.error(t('components.companies.form.createCompanyError'), {
        position: 'top-center',
      });
      onError?.();
    },
  });
}

interface UseAddContactsToCompanyMutationOptions {
  onSuccess?: (companyId: string, contactIds: string[]) => void;
  onError?: () => void;
  showToast?: boolean;
}

/**
 * Hook to add contacts to a company.
 * Optimistically updates the company members in the cache.
 */
type ContactInfo = { id: string; name: string; last_name: string };

export function useAddContactsToCompanyMutation({
  onSuccess,
  onError,
  showToast = false,
}: UseAddContactsToCompanyMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  // Store contact info for optimistic updates (set before mutation)
  const pendingContactsInfoRef = useRef<ContactInfo[]>([]);

  const mutation = $apiAllo.useMutation('patch', '/v1/company/{company}/add-contacts', {
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['get', '/v2/company'] });

      const previousData = queryClient.getQueriesData<InfiniteData<CompaniesResponse>>({
        queryKey: ['get', '/v2/company'],
      });

      // Optimistically add members to the company
      const companyId = variables.params.path.company;
      const contactIds = variables.body.contact_ids;
      const contactsInfo = pendingContactsInfoRef.current;

      queryClient.setQueriesData<InfiniteData<CompaniesResponse>>(
        { queryKey: ['get', '/v2/company'] },
        (oldData) => {
          if (!oldData || !oldData.pages?.length) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map((company) => {
                if (company.id !== companyId) {
                  return company;
                }
                // Add new members to existing ones
                const newMembers: CompanyMember[] = contactIds.map((id) => {
                  const info = contactsInfo.find((c) => c.id === id);
                  return {
                    id,
                    name: info?.name ?? '',
                    last_name: info?.last_name ?? '',
                  };
                });
                return {
                  ...company,
                  members: [...(company.members ?? []), ...newMembers],
                };
              }),
            })),
          };
        },
      );

      // Clear pending contacts info
      pendingContactsInfoRef.current = [];

      return { previousData };
    },
    onSuccess: (response, variables) => {
      // Track team member added
      analytics.track(ANALYTICS_EVENTS.COMPANIES.TEAM_MEMBER_ADDED, {
        company_id: variables.params.path.company,
        contact_ids: variables.body.contact_ids,
        count: variables.body.contact_ids.length,
      });

      // Update companies cache with the returned data (replaces optimistic data)
      const updatedCompanies = response.data;
      if (updatedCompanies && updatedCompanies.length > 0) {
        queryClient.setQueriesData<InfiniteData<CompaniesResponse>>(
          { queryKey: ['get', '/v2/company'] },
          (oldData) => {
            if (!oldData || !oldData.pages?.length) {
              return oldData;
            }

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data?.map((company) => {
                  const updatedCompany = updatedCompanies.find((c) => c.id === company.id);
                  return updatedCompany ?? company;
                }),
              })),
            };
          },
        );
      }

      onSuccess?.(variables.params.path.company, variables.body.contact_ids);
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      const ctx = context as
        | { previousData: [string[], InfiniteData<CompaniesResponse> | undefined][] }
        | undefined;
      if (ctx?.previousData) {
        for (const [queryKey, data] of ctx.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      if (showToast) {
        toast.error(t('components.companies.form.addContactsError'), {
          position: 'top-center',
        });
      }
      onError?.();
    },
  });

  const addContacts = useCallback(
    (companyId: string, contactIds: string[], contactsInfo?: ContactInfo[]) => {
      if (contactIds.length === 0) {
        return;
      }
      // Store contact info for optimistic update
      pendingContactsInfoRef.current = contactsInfo ?? [];
      mutation.mutate({
        params: { path: { company: companyId } },
        body: { contact_ids: contactIds },
      });
    },
    [mutation],
  );

  return {
    ...mutation,
    addContacts,
  };
}

/**
 * Hook to remove contacts from a company.
 * Optimistically updates the company members in the cache.
 */
export function useRemoveContactsFromCompanyMutation({
  onSuccess,
  onError,
  showToast = false,
}: UseAddContactsToCompanyMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  const mutation = $apiAllo.useMutation('patch', '/v1/company/{company}/remove-contacts', {
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['get', '/v2/company'] });

      const previousData = queryClient.getQueriesData<InfiniteData<CompaniesResponse>>({
        queryKey: ['get', '/v2/company'],
      });

      // Optimistically remove members from the company
      const companyId = variables.params.path.company;
      const contactIdsToRemove = variables.body.contact_ids;

      queryClient.setQueriesData<InfiniteData<CompaniesResponse>>(
        { queryKey: ['get', '/v2/company'] },
        (oldData) => {
          if (!oldData || !oldData.pages?.length) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map((company) => {
                if (company.id !== companyId) {
                  return company;
                }
                // Remove specified members
                return {
                  ...company,
                  members: (company.members ?? []).filter(
                    (m) => !contactIdsToRemove.includes(m.id ?? ''),
                  ),
                };
              }),
            })),
          };
        },
      );

      return { previousData };
    },
    onSuccess: (response, variables) => {
      // Track team member removed
      analytics.track(ANALYTICS_EVENTS.COMPANIES.TEAM_MEMBER_REMOVED, {
        company_id: variables.params.path.company,
        contact_ids: variables.body.contact_ids,
        count: variables.body.contact_ids.length,
      });

      // Update companies cache with the returned data
      const updatedCompanies = response.data;
      if (updatedCompanies && updatedCompanies.length > 0) {
        queryClient.setQueriesData<InfiniteData<CompaniesResponse>>(
          { queryKey: ['get', '/v2/company'] },
          (oldData) => {
            if (!oldData || !oldData.pages?.length) {
              return oldData;
            }

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data?.map((company) => {
                  const updatedCompany = updatedCompanies.find((c) => c.id === company.id);
                  return updatedCompany ?? company;
                }),
              })),
            };
          },
        );
      }

      onSuccess?.(variables.params.path.company, variables.body.contact_ids);
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      const ctx = context as
        | { previousData: [string[], InfiniteData<CompaniesResponse> | undefined][] }
        | undefined;
      if (ctx?.previousData) {
        for (const [queryKey, data] of ctx.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      if (showToast) {
        toast.error(t('components.companies.form.removeContactsError'), {
          position: 'top-center',
        });
      }
      onError?.();
    },
  });

  const removeContacts = useCallback(
    (companyId: string, contactIds: string[]) => {
      if (contactIds.length === 0) {
        return;
      }
      mutation.mutate({
        params: { path: { company: companyId } },
        body: { contact_ids: contactIds },
      });
    },
    [mutation],
  );

  return {
    ...mutation,
    removeContacts,
  };
}

/**
 * Hook to update company members.
 * Handles both adding new contacts and removing existing ones.
 */
export function useUpdateCompanyMembersMutation(
  options: UseAddContactsToCompanyMutationOptions = {},
) {
  const { addContacts } = useAddContactsToCompanyMutation(options);
  const { removeContacts } = useRemoveContactsFromCompanyMutation(options);

  return {
    addContacts,
    removeContacts,
  };
}

interface UseCreateContactAndAddToCompanyOptions {
  onSuccess?: (contact: components['schemas']['Contact']) => void;
  onError?: () => void;
}

interface CreateContactForCompanyParams {
  companyId: string;
  name: string;
  last_name: string;
  numbers: string[];
  company_id?: string | null;
  emails?: string[] | null;
  website?: string | null;
  job_title?: string | null;
  addresses?: string[] | null;
}

/**
 * Hook to create a contact and add it to a company.
 * Optimistically updates the company members cache immediately.
 */
export function useCreateContactAndAddToCompanyMutation({
  onSuccess,
  onError,
}: UseCreateContactAndAddToCompanyOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  // Store pending contact info for the optimistic update
  const pendingContactRef = useRef<{
    tempId: string;
    companyId: string;
    name: string;
    last_name: string;
  } | null>(null);

  const createContactMutation = $apiAllo.useMutation('post', '/v1/contact', {
    onMutate: async () => {
      // Optimistically add the contact to the company members
      const pending = pendingContactRef.current;
      if (!pending) {
        return {};
      }

      await queryClient.cancelQueries({ queryKey: ['get', '/v2/company'] });
      await queryClient.cancelQueries({ queryKey: ['get', '/v2/company/{companyPid}'] });

      const previousCompaniesData = queryClient.getQueriesData<InfiniteData<CompaniesResponse>>({
        queryKey: ['get', '/v2/company'],
      });

      const previousCompanyDetailData = queryClient.getQueriesData<{ data?: Company }>({
        queryKey: ['get', '/v2/company/{companyPid}'],
      });

      const newMember: CompanyMember = {
        id: pending.tempId,
        name: pending.name,
        last_name: pending.last_name,
      };

      // Add optimistic member to company (infinite query for table)
      queryClient.setQueriesData<InfiniteData<CompaniesResponse>>(
        { queryKey: ['get', '/v2/company'] },
        (oldData) => {
          if (!oldData || !oldData.pages?.length) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map((company) => {
                if (company.id !== pending.companyId) {
                  return company;
                }
                return {
                  ...company,
                  members: [...(company.members ?? []), newMember],
                };
              }),
            })),
          };
        },
      );

      // Add optimistic member to company (detail query for panel)
      queryClient.setQueriesData<{ data?: Company }>(
        { queryKey: ['get', '/v2/company/{companyPid}'] },
        (oldData) => {
          if (!oldData?.data || oldData.data.id !== pending.companyId) {
            return oldData;
          }

          return {
            ...oldData,
            data: {
              ...oldData.data,
              members: [...(oldData.data.members ?? []), newMember],
            },
          };
        },
      );

      return { previousCompaniesData, previousCompanyDetailData, pending };
    },
    onSuccess: async (newContact, _variables, context) => {
      const ctx = context as
        | {
            previousCompaniesData: [unknown[], InfiniteData<CompaniesResponse> | undefined][];
            previousCompanyDetailData: [unknown[], { data?: Company } | undefined][];
            pending: typeof pendingContactRef.current;
          }
        | undefined;
      const pending = ctx?.pending;

      if (!pending || !newContact.data?.id) {
        toast.success(t('components.contacts.form.createContactSuccess'), {
          position: 'top-center',
        });
        onSuccess?.(newContact.data as components['schemas']['Contact']);
        return;
      }

      // Replace temp ID with real ID in company members cache (infinite query for table)
      queryClient.setQueriesData<InfiniteData<CompaniesResponse>>(
        { queryKey: ['get', '/v2/company'] },
        (oldData) => {
          if (!oldData || !oldData.pages?.length) {
            return oldData;
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map((company) => {
                if (company.id !== pending.companyId) {
                  return company;
                }
                return {
                  ...company,
                  members: (company.members ?? []).map((member) =>
                    member.id === pending.tempId ? { ...member, id: newContact.data?.id } : member,
                  ),
                };
              }),
            })),
          };
        },
      );

      // Replace temp ID with real ID in company detail cache (for panel)
      queryClient.setQueriesData<{ data?: Company }>(
        { queryKey: ['get', '/v2/company/{companyPid}'] },
        (oldData) => {
          if (!oldData?.data || oldData.data.id !== pending.companyId) {
            return oldData;
          }

          return {
            ...oldData,
            data: {
              ...oldData.data,
              members: (oldData.data.members ?? []).map((member) =>
                member.id === pending.tempId ? { ...member, id: newContact.data?.id } : member,
              ),
            },
          };
        },
      );

      // Now add the contact to the company via API (fire and forget, cache is already updated)
      try {
        await $fetchAllo.PATCH('/v1/company/{company}/add-contacts', {
          params: { path: { company: pending.companyId } },
          body: { contact_ids: [newContact.data.id] },
        });
      } catch {
        // Silent fail - the optimistic update already shows the contact
        // A page refresh will reconcile the state
      }

      toast.success(t('components.contacts.form.createContactSuccess'), {
        position: 'top-center',
      });

      pendingContactRef.current = null;
      onSuccess?.(newContact.data as components['schemas']['Contact']);
    },
    onError: (_error, _variables, context) => {
      // Rollback optimistic update
      const ctx = context as
        | {
            previousCompaniesData: [unknown[], InfiniteData<CompaniesResponse> | undefined][];
            previousCompanyDetailData: [unknown[], { data?: Company } | undefined][];
          }
        | undefined;
      if (ctx?.previousCompaniesData) {
        for (const [queryKey, data] of ctx.previousCompaniesData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (ctx?.previousCompanyDetailData) {
        for (const [queryKey, data] of ctx.previousCompanyDetailData) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      toast.error(t('components.contacts.form.createContactError'), {
        position: 'top-center',
      });

      pendingContactRef.current = null;
      onError?.();
    },
  });

  const createContactAndAddToCompany = useCallback(
    (params: CreateContactForCompanyParams) => {
      // Generate a temporary ID for optimistic update
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      // Store pending info for optimistic update
      pendingContactRef.current = {
        tempId,
        companyId: params.companyId,
        name: params.name,
        last_name: params.last_name,
      };

      createContactMutation.mutate({
        body: {
          name: params.name,
          last_name: params.last_name,
          numbers: params.numbers,
          company_id: params.company_id,
          emails: params.emails,
          website: params.website,
          job_title: params.job_title,
          addresses: params.addresses,
        },
      });
    },
    [createContactMutation],
  );

  return {
    ...createContactMutation,
    createContactAndAddToCompany,
  };
}
